import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  InputContainer,
  Input,
  DropdownButton,
  SuggestionsList,
  SuggestionsScroll,
  LoaderContainer,
  FloatingLabel,
  ErrorMessage,
  ClearButton, 
} from "./index.styled";
import { ReactComponent as Arrow } from "../../Assets/Icons/Arrow.svg";
import { Loader } from "../../App";
import { ReactComponent as ErrorInfo } from "../../Assets/Icons/ErrorInfo.svg";
import { states } from "../../Configs/config";
import { ReactComponent as CloseIcon } from "../../Assets/Icons/CrossSolid.svg";

const CustomInput = ({
  placeholder,
  type,
  dropdown,
  value,
  name,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  error,
  disabled = false,
  suggestions = [],
  height,
  suggestionLoader,
  label = "",
  parent = "",
  onNoMatch,
  filteredData = {},
  lastActiveInputName,
  setLastActiveInputName,
  showError = true,
  // When true, the input behaves as a fixed-options combobox: it always shows
  // the complete option list (selected option pinned to the top), filters only
  // while the user is actively typing, and never writes partial text into the
  // parent state. Used for fixed dropdowns like State / Facility / Ownership.
  comboMode = false,
  // Optional subset (in the field's raw value domain) of options that are
  // currently valid for the applied filters. Options outside this set are shown
  // but disabled/dimmed. When omitted/empty, every option is selectable.
  enabledOptions,
}) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [noMatch, setNoMatch] = useState(false);
  // Holds the in-progress search text for comboMode. null means "not typing"
  // (show the selected value in the field and the full list in the dropdown).
  const [typedText, setTypedText] = useState(null);
  const inputRef = useRef(null);
  const suggestionList = suggestions.map((item) => `${states[item]} (${item})`);

  // Master option list for comboMode (state options are rendered as "Name (ABBR)").
  const comboBaseOptions = name === "state" ? suggestionList : suggestions;

  const getComboOptions = () => {
    let list = Array.isArray(comboBaseOptions) ? [...comboBaseOptions] : [];
    if (typedText) {
      const query = typedText.toLowerCase();
      list = list.filter(
        (item) =>
          typeof item === "string" && item.toLowerCase().includes(query),
      );
    }
    // Pin the currently selected option to the top so it's clearly marked.
    if (value) {
      const idx = list.findIndex((item) => item === value);
      if (idx > 0) {
        list = [list[idx], ...list.slice(0, idx), ...list.slice(idx + 1)];
      }
    }
    return list;
  };

  const displayedSuggestions = comboMode ? getComboOptions() : filteredSuggestions;

  // Lookup of options that are valid for the current filters (case-insensitive,
  // normalized to the displayed "Name (ABBR)" form for state). null => no
  // restriction, so every option stays selectable.
  const enabledLookup = useMemo(() => {
    if (!comboMode || !Array.isArray(enabledOptions) || enabledOptions.length === 0) {
      return null;
    }
    const normalize = (item) =>
      typeof item === "string" ? item.toLowerCase() : item;
    const normalizedOptions =
      name === "state"
        ? enabledOptions.map((item) =>
            states[item] !== undefined ? `${states[item]} (${item})` : item,
          )
        : enabledOptions;
    return new Set(normalizedOptions.map(normalize));
  }, [comboMode, enabledOptions, name]);

  const isOptionEnabled = (item) =>
    !enabledLookup ||
    item === value ||
    enabledLookup.has(typeof item === "string" ? item.toLowerCase() : item);

  const toggleSuggestion = () => {
    if (comboMode) {
      // Opening the dropdown always reveals the full list.
      if (!showSuggestions) setTypedText(null);
      setShowSuggestions(!showSuggestions);
      return;
    }
    if (!showSuggestions) {
      setFilteredSuggestions(
        filteredSuggestions.length > 0
          ? filteredSuggestions
          : name === "state"
            ? suggestionList
            : suggestions,
      );
    }
    setShowSuggestions(!showSuggestions);
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;

    if (comboMode) {
      // Track the search text locally only; the parent state is updated solely
      // on explicit selection so typing never leaves partial text behind.
      setTypedText(inputValue);
      setShowSuggestions(true);
      // Exception: emptying the field (e.g. backspacing out the selection)
      // commits the clear so the selected option is actually removed instead of
      // being restored from the parent value on blur.
      if (inputValue === "") {
        onChange(name, "", true);
      }
      return;
    }

    onChange(name, inputValue);

    const sourceList = name === "state" ? suggestionList : suggestions;

    const filtered = sourceList.filter(
      (item) =>
        typeof inputValue === "string" &&
        typeof item === "string" &&
        item.toLowerCase().includes(inputValue.toLowerCase()),
    );

    setFilteredSuggestions(filtered);
    setShowSuggestions(true);
    const hasNoMatch = filtered.length === 0;
    setNoMatch(hasNoMatch);

    if (onNoMatch) {
      onNoMatch(name, hasNoMatch);
    }
    if (!inputValue) {
      setFilteredSuggestions(name === "state" ? suggestionList : suggestions);
      setShowSuggestions(false);
      setNoMatch(false);
      if (onNoMatch) {
        onNoMatch(name, false);
      }
    }
  };

  const handleSelect = (selectedValue) => {
    if (name === "state") {
      onChange(name, selectedValue.split("(")[1].slice(0, -1), true);
    } else {
      onChange(name, selectedValue, true);
    }
    setShowSuggestions(false);
    setNoMatch(false);
    setTypedText(null);
  };

  const handleClear = () => {
    onChange(name, ""); // Clear the input value
    setFilteredSuggestions(name === "state" ? suggestionList : suggestions);
    setShowSuggestions(false);
    setNoMatch(false);
  };

  useEffect(() => {
    if (!suggestionLoader) {
      setFilteredSuggestions((prev) => {
        const newSuggestions = name === "state" ? suggestionList : suggestions;
        return JSON.stringify(prev) !== JSON.stringify(newSuggestions)
          ? newSuggestions
          : prev;
      });
    }
  }, [suggestions, suggestionLoader, name, suggestionList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Container>
      <InputContainer
        ref={inputRef}
        error={(error || noMatch) && showError}
        disabled={lastActiveInputName == name ? false : disabled}
      >
        <Input
          type={type || "text"}
          placeholder={""}
          value={comboMode ? (typedText !== null ? typedText : value || "") : value}
          onChange={!disabled ? handleChange : undefined}
          height={2.5}
          disabled={lastActiveInputName == name ? false : disabled}
          onFocus={() => {
            if (!disabled) {
              setLastActiveInputName?.(name);
              setIsFocused(true);
              if (comboMode) {
                setShowSuggestions(true);
              } else if (
                value &&
                (name === "state"
                  ? suggestionList.length
                  : suggestions.length) > 0
              ) {
                if(lastActiveInputName !== name){
                  setShowSuggestions(true);
                }
              }
            }
            onFocus?.();
          }}
          onBlur={() => {
            if (!disabled) {
              setIsFocused(false);
              setTimeout(() => {
                setShowSuggestions(false);
                // Discard any unsubmitted search text and revert to showing
                // the selected value.
                if (comboMode) setTypedText(null);
              }, 250);
            }
            onBlur?.();
          }}
          onKeyDown={onKeyDown}
        />
        <FloatingLabel>
          {isFocused || value ? label : placeholder || "Enter value"}
        </FloatingLabel>

        {/* {value && (
          <ClearButton onClick={handleClear}>
            <CloseIcon
            style={{
              cursor: "pointer",
              fontSize: viewSizeCalculator(2),
              width: viewSizeCalculator(1.5),
              height: viewSizeCalculator(1.5),
            }}
          />
          </ClearButton>
        )} */}

        {dropdown && (
          <DropdownButton onClick={toggleSuggestion}>
            <Arrow />
          </DropdownButton>
        )}
        <SuggestionsList
          isOpen={
            showSuggestions &&
            (displayedSuggestions.length > 0 ||
              (!comboMode && name === "state"))
          }
          disabled={lastActiveInputName == name ? false : disabled}
        >
          <SuggestionsScroll>
            {suggestionLoader ? (
              <LoaderContainer>
                <Loader />
              </LoaderContainer>
            ) : displayedSuggestions.length > 0 ? (
              displayedSuggestions.map((item, index) => {
                const enabled = isOptionEnabled(item);
                return (
                  <li
                    key={index}
                    onClick={enabled ? () => handleSelect(item) : undefined}
                    aria-disabled={!enabled}
                    className={[
                      comboMode && item === value ? "selected" : "",
                      !enabled ? "disabled" : "",
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined}
                  >
                    {item}
                  </li>
                );
              })
            ) : (
              <li>No Suggestion</li>
            )}
          </SuggestionsScroll>
        </SuggestionsList>
      </InputContainer>

      {noMatch && name === "cbsa" && (
        <ErrorMessage>
          <ErrorInfo />
          The CBSA does not align with the selected region
        </ErrorMessage>
      )}

      {noMatch && name === "hospital_name" && (
        <ErrorMessage>
          <ErrorInfo />
          The name does not match the selected location
        </ErrorMessage>
      )}

      {noMatch && name === "zip_code" && (
        <>
          {filteredData.state !== "" ? (
            <ErrorMessage>
              <ErrorInfo />
              The ZIP code does not belong to the chosen state
            </ErrorMessage>
          ) : filteredData.cbsa !== "" ? (
            <ErrorMessage>
              <ErrorInfo />
              The ZIP code doesn't fall under the selected CBSA
            </ErrorMessage>
          ) : filteredData.hospital_name !== "" ? (
            <ErrorMessage>
              <ErrorInfo />
              The ZIP code doesn't match hospital location
            </ErrorMessage>
          ) : <ErrorMessage>
                <ErrorInfo />
                The ZIP code does not match the selected location
              </ErrorMessage>
          }
        </>
      )}
    </Container>
  );
};

export default CustomInput;